// The browser half: an `AudioContext` behind the port.
//
// **Nothing here touches a browser global at import time.** The context is made
// inside `unlock()`, which is the rule that lets a node test import this module
// by accident and lets `engine.ts` hold one of these from module scope. Every
// path is guarded the same way: no context, no `AudioContext` constructor, a
// fetch that fails, a decode the browser refuses — all of them degrade to a
// port that answers politely and makes no noise. `board-smoke` fails on a
// console error, so the only noise this file may make is a single `console.warn`
// per url that would not load.
//
// The graph is one deep: every voice hangs off its bus, every bus off the
// master, the master off the destination. That is what makes a volume slider
// one `setBusGain` rather than a walk over live sources.

import type { AudioPort, VoiceHandle } from "./port";
import type { Bus } from "./sounds";

/**
 * How long a gain change takes unless somebody says otherwise.
 *
 * Thirty milliseconds: short enough to read as instant, long enough that the
 * step in the waveform is a ramp rather than an edge. A `setValueAtTime` on a
 * live voice clicks, audibly, on every slider drag.
 */
const RAMP_SECONDS = 0.03;

/** The buses, in the order the graph builds them. */
const BUSES: readonly Bus[] = ["music", "ambience", "sfx", "ui"];

/** Gain nodes cannot ramp to exactly zero on an exponential; everything here is linear, so they can. */
const SILENT = 0;

type ContextCtor = new () => AudioContext;

/** `webkitAudioContext` is still what an older iOS Safari offers. */
function contextCtor(): ContextCtor | null {
  const scope = globalThis as unknown as {
    AudioContext?: ContextCtor;
    webkitAudioContext?: ContextCtor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

/** A handle over a voice that has already gone: everything is a no-op. */
const DEAD_VOICE: VoiceHandle = {
  stop: () => {},
  setGain: () => {}
};

export class WebAudioPort implements AudioPort {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private busNodes = new Map<Bus, GainNode>();

  /**
   * Gains asked for before there was anything to apply them to.
   *
   * The settings are read and applied at mount; the context cannot exist until
   * the first gesture. Without this, everybody's stored volumes would be
   * silently discarded and the board would open at full.
   */
  private gains = new Map<Bus | "master", number>();

  /** One warning per url, however many times a sound is asked for. */
  private warned = new Set<string>();

  get unlocked(): boolean {
    return this.ctx !== null && this.ctx.state !== "suspended";
  }

  unlock(): void {
    if (!this.ctx) {
      const Ctor = contextCtor();
      // No Web Audio at all — an old browser, or a node test that imported
      // this by accident. Inert is the whole answer; there is nothing to say.
      if (!Ctor) return;
      try {
        this.ctx = new Ctor();
      } catch {
        return;
      }
      this.build(this.ctx);
    }
    // A context can be made in a suspended state and only a gesture resumes
    // it. The rejection is swallowed: a refused resume is not a console error.
    void this.ctx.resume?.().catch(() => {});
  }

  now(): number {
    return this.ctx?.currentTime ?? 0;
  }

  async load(url: string): Promise<unknown | null> {
    const ctx = this.ctx;
    if (!ctx) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return this.warn(url, `${response.status}`);
      return await ctx.decodeAudioData(await response.arrayBuffer());
    } catch (_error) {
      return this.warn(url, "could not be decoded");
    }
  }

  play(
    buffer: unknown,
    opts: {
      bus: Bus;
      gain: number;
      pan: number;
      rate: number;
      loop?: boolean;
      delaySec?: number;
      onEnded?: () => void;
    }
  ): VoiceHandle {
    const ctx = this.ctx;
    // The caller is told it ended, or an engine counting voices leaks one for
    // every sound it asked for while the context was away.
    if (!ctx || !isBuffer(buffer)) {
      opts.onEnded?.();
      return DEAD_VOICE;
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = opts.rate;
    if (opts.loop) source.loop = true;

    const gain = ctx.createGain();
    gain.gain.value = opts.gain;

    // `createStereoPanner` is the one node in this graph an older browser may
    // not have. Without it the sound plays in the middle, which is a worse
    // placement and not a failure.
    let tail: AudioNode = gain;
    if (typeof ctx.createStereoPanner === "function" && opts.pan !== 0) {
      const panner = ctx.createStereoPanner();
      panner.pan.value = opts.pan;
      gain.connect(panner);
      tail = panner;
    }

    source.connect(gain);
    tail.connect(this.busNodes.get(opts.bus) ?? this.master ?? ctx.destination);

    if (opts.onEnded) source.onended = opts.onEnded;
    try {
      source.start(ctx.currentTime + (opts.delaySec ?? 0));
    } catch {
      opts.onEnded?.();
      return DEAD_VOICE;
    }

    return {
      stop: (fadeSec = RAMP_SECONDS) => {
        const at = ctx.currentTime;
        try {
          gain.gain.cancelScheduledValues(at);
          gain.gain.setValueAtTime(gain.gain.value, at);
          gain.gain.linearRampToValueAtTime(SILENT, at + fadeSec);
          source.stop(at + fadeSec);
        } catch {
          // Already stopped. Asking twice is ordinary — a fade and an ending
          // race — and is not worth an exception.
        }
      },
      setGain: (value: number, rampSec = RAMP_SECONDS) => {
        ramp(gain.gain, value, ctx.currentTime, rampSec);
      }
    };
  }

  setBusGain(bus: Bus | "master", gain: number, rampSec = RAMP_SECONDS): void {
    this.gains.set(bus, gain);
    const node = bus === "master" ? this.master : this.busNodes.get(bus);
    if (!node || !this.ctx) return;
    ramp(node.gain, gain, this.ctx.currentTime, rampSec);
  }

  /** `bus gains → master gain → destination`, once, when the context arrives. */
  private build(ctx: AudioContext): void {
    const master = ctx.createGain();
    master.gain.value = this.gains.get("master") ?? 1;
    master.connect(ctx.destination);
    this.master = master;

    for (const bus of BUSES) {
      const node = ctx.createGain();
      node.gain.value = this.gains.get(bus) ?? 1;
      node.connect(master);
      this.busNodes.set(bus, node);
    }
  }

  private warn(url: string, why: string): null {
    if (!this.warned.has(url)) {
      this.warned.add(url);
      // A warn and never an error: a smoke fails the whole run on a console
      // error, and a game with one silent sound is a game.
      console.warn(`sound: ${url} ${why}; playing silently`);
    }
    return null;
  }
}

function isBuffer(buffer: unknown): buffer is AudioBuffer {
  return typeof buffer === "object" && buffer !== null && "duration" in buffer;
}

/** Linear, never exponential: an exponential ramp cannot reach or leave zero. */
function ramp(param: AudioParam, value: number, at: number, seconds: number): void {
  try {
    param.cancelScheduledValues(at);
    param.setValueAtTime(param.value, at);
    param.linearRampToValueAtTime(value, at + Math.max(0, seconds));
  } catch {
    param.value = value;
  }
}
